import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import {
    IBetslipMessages,
    IBetslipMessagesProviderService,
    ILineSwitcherMessages,
    ITeaserBetMessages,
    formatObject,
} from '@frontend/sports/betslip/core/feature';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';

export type IDigitalBetslipMessages = IBetslipMessages & { lineSwitcher: ILineSwitcherMessages; teaserBet: ITeaserBetMessages };

@Injectable()
export class BetslipMessageProviderService implements IBetslipMessagesProviderService {
    private messages: IDigitalBetslipMessages;

    constructor(
        private sitecore: Sitecore,
        private sanitizer: DomSanitizer,
    ) {}

    getMessages(): IDigitalBetslipMessages {
        if (!this.messages) {
            this.initMessages();
        }

        return this.messages;
    }

    private initMessages(): void {
        this.messages = {
            betTypes: {
                Single: this.sitecore.betslip.messages.Single,
                Combo: this.sitecore.betslip.messages.Combo,
                System: this.sitecore.betslip.messages.System,
                Teaser: this.sitecore.betslip.messages.Teaser,
            },
            racingBetTypes: {
                betTypeWin: this.sitecore.betslip.betPlacementRequest.BetTypeWin,
                betTypeWinAlt: '',
                betTypeForecast: this.sitecore.betslip.betPlacementRequest.BetTypeForecast,
                betTypeForecastAlt: '',
                betTypeTricast: this.sitecore.betslip.betPlacementRequest.BetTypeTricast,
                betTypeTricastAlt: '',
                betTypeCombinationForecast: this.sitecore.betslip.betPlacementRequest.BetTypeCombinationForecast,
                betTypeCombinationForecastAlt: '',
                betTypeCombinationTricast: this.sitecore.betslip.betPlacementRequest.BetTypeCombinationTricast,
                betTypeCombinationTricastAlt: '',
            },
            racingMarketNames: {
                win: this.sitecore.horseRacing.markets.Win,
                winAlt: '',
                forecast: this.sitecore.horseRacing.markets.Forecast,
                forecastAlt: '',
                tricast: this.sitecore.horseRacing.markets.Tricast,
                tricastAlt: '',
                combinationForecast: this.sitecore.horseRacing.markets.CombinationForecast,
                combinationForecastAlt: '',
                combinationTricast: this.sitecore.horseRacing.markets.CombinationTricast,
                combinationTricastAlt: '',
                bettingWithout: this.sitecore.betslip.receiptRacingMarkets.BettingWithout,
                winBy1Lengths: this.sitecore.betslip.receiptRacingMarkets.WinBy1Lengths,
                winBy3Lengths: this.sitecore.betslip.receiptRacingMarkets.WinBy3Lengths,
                winEw: this.sitecore.betslip.receiptRacingMarkets.WinEw,
                winEwExtraPlace: this.sitecore.betslip.receiptRacingMarkets.WinEwExtraPlace,
                winnerByHowFar: this.sitecore.betslip.receiptRacingMarkets.WinnerByHowFar,
                toFinish2: this.sitecore.betslip.receiptRacingMarkets.ToFinish2,
                toFinish3: this.sitecore.betslip.receiptRacingMarkets.ToFinish3,
                toFinish4: this.sitecore.betslip.receiptRacingMarkets.ToFinish4,
                topFinish2: this.sitecore.betslip.receiptRacingMarkets.TopFinish2,
                topFinish3: this.sitecore.betslip.receiptRacingMarkets.TopFinish3,
                topFinish4: this.sitecore.betslip.receiptRacingMarkets.TopFinish4,
                insurance2Places: this.sitecore.betslip.receiptRacingMarkets.Insurance2Places,
                insurance3Places: this.sitecore.betslip.receiptRacingMarkets.Insurance3Places,
                insurance4Places: this.sitecore.betslip.receiptRacingMarkets.Insurance4Places,
                freeFormed: this.sitecore.betslip.receiptRacingMarkets.FreeFormed,
                optionNameWinnerByHowFar: this.sitecore.horseRacing.common.WinningHowFarOption,
                specials: this.sitecore.betslip.receiptRacingMarkets.Specials,
                lengths: this.sitecore.horseRacing.common.Lengths,
                overAndIncluding: this.sitecore.horseRacing.markets.OverAndIncluding,
                under: this.sitecore.horseRacing.markets.Under,
            },
            validation: {
                ...this.sitecore.betslip.validation,
                BetBuilderMaxSelectionsPerPick: this.sitecore.betBuilderEntainUI.messages?.PickLimitErrorMessage ?? '',
            },
            editBet: this.sitecore.betslip.editBet,
            earlyPayoutErrors: this.sitecore.earlyPayoutErrors,
            common: this.sitecore.betslip.messages,
            numpad: this.sitecore.betslip.numpad,
            errors: this.sitecore.betslip.errors,
            crmTokens: this.sitecore.crmTokens,
            globalMessages: this.sitecore.globalMessages,
            betStationBetslip: {
                receipt: {
                    BetBuilderPickTitle: '',
                    BetTypeCombinationForecast: '',
                    BetTypeCombinationTricast: '',
                    BetTypeEachWay: '',
                    BetTypeForecast: '',
                    BetTypeTricast: '',
                    BetTypeWin: '',
                    PrintPickFirstPosition: '',
                    PrintPickSecondPosition: '',
                    PrintPickThirdPosition: '',
                    PrintTypeAccaBoost: '',
                    PrintTypeAccumulator: '',
                    PrintTypeEachWayAccumulator: '',
                    PrintTypeSingles: '',
                    StartingPrice: '',
                },
                receiptSystemModes: {
                    canadian: '',
                    goliath: '',
                    heinz: '',
                    lucky15: '',
                    lucky31: '',
                    lucky63: '',
                    patent: '',
                    superheinz: '',
                    system2of3: '',
                    system2of4: '',
                    system2of5: '',
                    system2of6: '',
                    system2of7: '',
                    system2of8: '',
                    system3of4: '',
                    system3of5: '',
                    system3of6: '',
                    system3of7: '',
                    system3of8: '',
                    system4of5: '',
                    system4of6: '',
                    system4of7: '',
                    system4of8: '',
                    system5of6: '',
                    system5of7: '',
                    system5of8: '',
                    system6of7: '',
                    system6of8: '',
                    system7of8: '',
                    trixie: '',
                    yankee: '',
                },
                receiptRacingMarkets: {
                    CombinationForecast: '',
                    CombinationTricast: '',
                    Forecast: '',
                    Freeform: '',
                    Specials: '',
                    Tricast: '',
                    Win: '',
                    WinOnly: '',
                    WinShort: '',
                },
                messages: {
                    BetBuilderPickTitle: '',
                    EachWaySetToAll: '',
                    EachWayWasSet: '',
                    NumberOfBets: '',
                    NumberOfLegs: '',
                    PlacingStateMessage: '',
                    PlacedStateMessage: '',
                    PrintPickFirstPosition: '',
                    PrintPickSecondPosition: '',
                    PrintPickThirdPosition: '',
                    PrintTypeAccaBoost: '',
                    PrintTypeAccumulator: '',
                    PrintTypeEachWayAccumulator: '',
                    PrintTypeSingles: '',
                    RacePickEachWayButton: '',
                    RacePickStartingPriceButton: '',
                    RacePickWinButton: '',
                    TypeAccumulator: '',
                    TypeCombinations: '',
                    TypeEachWayAccumulator: '',
                    TypeSingles: '',
                    overallMaxWinPerUserExceeded_StakeUpdated_Title: '',
                    ReduceSelections: '',
                    UpdateStake: '',
                    SingleEventSelections: '',
                    MultiEventSelections: '',
                },
            },
            systemBets: this.sitecore.systemBets,
            betBuilder: {
                marketName: this.sitecore.globalMessages.BetBuilderMarketText,
            },
            links: this.sitecore.betslip.links,
            systemModes: this.sitecore.betslip.systemModes,
            betPlacementRequest: this.sitecore.betslip.betPlacementRequest,
            summary: {
                stake: this.sitecore.betslip.messages.Stake,
                stakePerBet: this.sitecore.betslip.messages.PerBet,
                bets: this.sitecore.betslip.messages.Bets,
                totalStake: this.sitecore.betslip.messages.TotalStake,
                teaserStake: this.sitecore.betslip.messages.TeaserStake,
                totalOdds: this.sitecore.betslip.messages.TotalOdds,
                possibleWinnings: this.sitecore.betslip.messages.PossibleWinnings,
                possibleWinningsNet: this.sitecore.betslip.messages.PossibleWinningsNet,
                totalReturns: this.sitecore.betslip.linear.TotalReturns,
                totalReturnsNet: this.sitecore.betslip.linear.TotalReturnsNet,
                ctaButton: {
                    acceptChanges: '',
                    acceptChangesAndPlaceBet: '',
                    avgOdds: '',
                    betsWillBeRemoved: '',
                    enterValidStake: '',
                    insertCacheToPlaceBet: '',
                    maxReturn: '',
                    odds: '',
                    placeBet: '',
                    placeFreeBet: '',
                    resolveErrorToProceed: '',
                    totalOdds: '',
                    viewChangesInBetslip: '',
                },
            },
            taxationInfo: {
                possibleWinnings: this.sitecore.betslip.messages.PossibleWinnings,
                possibleWinningsNet: this.sitecore.betslip.messages.PossibleWinningsNet,
                boostedPossibleWinnings: this.sitecore.crmTokens.oddsBoost.betslipSummary.TaxationSummaryBoostedWinnings,
                notBoostedPossibleWinnings: this.sitecore.crmTokens.oddsBoost.betslipSummary.TaxationSummaryNonBoostedWinnings,
                taxationHelpUrl: this.sitecore.betslip.links.help.taxation_de?.url ?? '',
                closeButton: this.sitecore.betslip.editBet.infoConfirm,
                taxationHelpText: this.sanitizer.bypassSecurityTrustHtml(
                    formatObject(this.sitecore.betslip.editBet.taxationFurtherInfoText, [
                        `<span>${this.sitecore.betslip.editBet.bwinHelpPage}</span>`,
                    ]),
                ),
                taxationNoRate: this.sitecore.betslip.messages.TaxMessageWhenNoPercentage,
                taxationRate: this.sitecore.betslip.messages.TaxOnWinnings2,
            },
            result: {
                goodLuck: this.sitecore.betslip.messages.GoodLuck,
                numberOfBets: this.sitecore.betslip.messages.NumberOfBets,
                possibleWinningsNet: this.sitecore.betslip.messages.PossibleWinningsNet,
                possibleWinnings: this.sitecore.betslip.messages.PossibleWinnings,
                totalStake: this.sitecore.betslip.messages.TotalStake,
                teaserStake: this.sitecore.betslip.messages.TeaserStake,
                stake: this.sitecore.betslip.messages.Stake,
                possibleWinningsGross: this.sitecore.betslip.messages.PossibleWinningsGross,
                tax: this.sitecore.betslip.messages.Tax,
                totalOdds: this.sitecore.betslip.messages.TotalOdds,
                hide: this.sitecore.globalMessages.Hide,
                show: this.sitecore.globalMessages.Show,
                betDetails: this.sitecore.betslip.editBet.betDetails,
                retainPlaced: this.sitecore.betslip.messages.RetainPlaced,
            },
            settings: {
                title: this.sitecore.betslip.messages.BettingOptions,
                acceptAnyOdds: this.sitecore.betslip.messages.AcceptAny,
                acceptAnyExperience: this.sitecore.betslip.messages.AcceptAnyExperience,
                acceptHigher: this.sitecore.betslip.messages.AcceptHigher,
                acceptNoChanges: this.sitecore.betslip.messages.AcceptNoChanges,
                acceptTac: this.sitecore.betslip.messages.AcceptTac,
                appNotification: this.sitecore.betslip.messages.AppNotification,
                emailNotification: this.sitecore.betslip.messages.EmailNotification,
            },
            modalDialogs: this.sitecore.modalDialogs,
            overask: this.sitecore.betslip.overask,
            teaserBet: this.sitecore.betslip.teaserBet,
            lineSwitcher: this.sitecore.betslip.lineSwitcher,
            affiliate: this.sitecore.betslip.affiliate,
            alternateLineMessages: this.sitecore.betslip.alternateLineMessages,
        };
    }
}
